import React, { FC, memo, useMemo, useCallback, useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import ErrorMessage from '@minecraft.error-message';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import { GetOrganizationProfileListsDoc, GetOrganizationProfileListsQuery } from '@minecraft.graphql-operations';
import { FilterData } from '../../../shared/types';
import { generateSelectOptionsBasedOnField } from '../../../shared/utils';

type Props = FilterData;

const RosterLists: FC<Props> = ({ name, label }) => {
  const [fetchMoreErr, setFetchMoreErr] = useState<string>(null);
  const [rosterLists, setRosterLists] = useState<
    GetOrganizationProfileListsQuery['getOrganizationProfileLists']['objects']
  >([]);
  const [getRosterLists, { data, error, loading, fetchMore }] = useLazyQuery(GetOrganizationProfileListsDoc, {});

  const handleListsFetch = useCallback(
    (searchValue: string, offset: number, limit: number): void => {
      getRosterLists({ variables: { pageInput: { pageNumber: offset, pageSize: limit }, searchKeyword: searchValue } });
    },
    [getRosterLists]
  );

  const handleListsFetchMore = useCallback(
    async (searchValue: string, offset: number, limit: number) => {
      try {
        const { data } = await fetchMore({
          variables: { pageInput: { pageNumber: offset, pageSize: limit }, searchKeyword: searchValue },
        });
        if (data?.getOrganizationProfileLists?.objects) {
          setRosterLists((prev) => [...prev, ...data.getOrganizationProfileLists.objects]);
        }
      } catch (e) {
        setFetchMoreErr(e?.message);
        console.error('error fetching more roster lists', e);
      }
    },
    [fetchMore]
  );

  useEffect(() => {
    if (!data?.getOrganizationProfileLists?.objects) {
      setRosterLists([]);
    } else {
      setRosterLists(data?.getOrganizationProfileLists?.objects);
    }
  }, [data]);

  const rosterOptions = useMemo(() => {
    return generateSelectOptionsBasedOnField(rosterLists, 'name', false, undefined, false);
  }, [rosterLists]);

  return (
    <DropdownWithChildren
      withFormik
      withSearch
      withSelectAll
      isMultipleSelect
      withSeeMoreButton
      withAutocomplete
      isLoading={loading}
      name={name}
      toggleLabel={label}
      options={rosterOptions}
      onOptionsFetch={(searchValue, offset, limit) => handleListsFetch(searchValue, offset, limit)}
      onOptionsFetchMore={(searchValue, offset, limit) => handleListsFetchMore(searchValue, offset, limit)}
      optionsTotalCount={data?.getOrganizationProfileLists?.totalCount}
      initialOffset={1}
      initialLimit={100}
    >
      {(error || fetchMoreErr) && <ErrorMessage error={error || { message: fetchMoreErr }} />}
    </DropdownWithChildren>
  );
};

export default memo(RosterLists);
